import { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { usd } from "../data";
import { AdminReturns } from "./AdminReturns";
import { AdminTelegram } from "./AdminTelegram";
import { SupportInbox } from "./SupportInbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gtax/admin`;
const WALLET_LABEL = { usdt_bep20: "USDT BEP-20", usdc_erc20: "USDC ERC-20", usdt_trc20: "USDT TRC-20" };
const BADGE = { pending: "bg-[#E8DDC4] text-[#6B5F4B]", approved: "bg-[#8F6B22] text-white", rejected: "bg-[#A0523F] text-white", not_required: "bg-[#2A2A2A] text-white" };
const errMsg = (e) => {
  const d = e.response?.data?.detail;
  return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Something went wrong.";
};

const Field = ({ label, ...p }) => (
  <div><label className="block text-xs tracking-widest uppercase text-[color:var(--mute)]">{label}</label><input {...p} className="w-full py-3 text-lg" /></div>
);

const Login = ({ onAuthed }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stage, setStage] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const login = async (e) => {
    e.preventDefault(); setBusy(true);
    try { setStage((await axios.post(`${API}/login`, { email, password })).data); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/verify-2fa`, { temp_token: stage.temp_token, code });
      sessionStorage.setItem("gtax_admin", data.access_token); onAuthed(data.access_token);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (!stage) return (
    <form onSubmit={login} className="gx-card p-10 max-w-md space-y-8" data-testid="gtax-admin-login-form">
      <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="gtax-admin-email" />
      <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="gtax-admin-password" />
      <button disabled={busy} className="gx-btn" data-testid="gtax-admin-login-submit">Sign in</button>
    </form>
  );
  return (
    <form onSubmit={verify} className="gx-card p-10 max-w-md space-y-6" data-testid="gtax-admin-2fa-form">
      {stage.stage === "enroll" && (
        <div className="space-y-4" data-testid="gtax-admin-enroll">
          <p>Scan this with Google Authenticator (or any authenticator app), then enter the 6-digit code.</p>
          <div className="bg-white p-4 inline-block rounded"><QRCodeSVG value={stage.otpauth_uri} size={180} /></div>
          <p className="text-xs font-mono break-all" data-testid="gtax-admin-secret">Key: {stage.secret}</p>
        </div>
      )}
      <Field label="6-digit code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} data-testid="gtax-admin-code" />
      <button disabled={busy || code.length !== 6} className="gx-btn disabled:opacity-40" data-testid="gtax-admin-verify">Verify</button>
    </form>
  );
};

const DirectInvite = ({ h }) => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/direct-invite`, h).then((r) => setD(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const copy = (v) => navigator.clipboard.writeText(v).then(() => toast.success("Link copied"));
  return (
    <div className="gx-card p-6 space-y-3" data-testid="gtax-admin-direct-invite">
      <div className="gx-eyebrow">Direct invite · partners and people you invite yourself (no referrer)</div>
      {[["Website", d.website, "web"], ["Telegram", d.telegram, "tg"]].map(([l, v, id]) => (
        <div key={id} className="flex flex-wrap items-center gap-3">
          <span className="w-24 text-sm text-[color:var(--mute)]">{l}</span>
          <code className="text-sm break-all flex-1" data-testid={`gtax-admin-direct-${id}`}>{v}</code>
          <button onClick={() => copy(v)} className="gx-ghost !px-3 !py-1" data-testid={`gtax-admin-direct-copy-${id}`}>Copy</button>
        </div>
      ))}
    </div>
  );
};

const Dashboard = ({ token, logout }) => {
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = () => Promise.all([axios.get(`${API}/clients`, h), axios.get(`${API}/contacts`, h)])
    .then(([a, b]) => { setRows(a.data); setContacts(b.data); }).catch((e) => { if (e.response?.status === 401) logout(); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const decide = async (ref, status) => {
    try { await axios.post(`${API}/clients/${ref}/kyc`, { status }, h); toast.success(`${ref} ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-14" data-testid="gtax-admin-dashboard">
      <div className="flex justify-between items-center">
        <h2 className="gx-serif text-4xl">Members ({rows.length})</h2>
        <button onClick={logout} className="gx-ghost" data-testid="gtax-admin-logout">Sign out</button>
      </div>
      <DirectInvite h={h} />
      <SupportInbox h={h} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-[color:var(--mute)] border-b border-[color:var(--line)]">
            <th>Reference</th><th>Name / contact</th><th>Tier</th><th className="text-right">Amount</th><th>Signed</th><th>KYC</th><th>Deposit TXID</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.reference} className="border-b border-[color:var(--line)] align-top" data-testid={`gtax-admin-row-${r.reference}`}>
                <td className="font-mono">{r.reference}</td>
                <td>{r.full_name}<div className="text-xs text-[color:var(--mute)]">{r.email} · {r.phone} · {r.country}</div>{r.wallet && <div className="text-xs text-[color:var(--mute)] font-mono break-all max-w-[260px]" data-testid={`gtax-admin-wallet-${r.reference}`}>Redemption: {WALLET_LABEL[r.wallet_network] || ""} {r.wallet}</div>}</td>
                <td>{r.tier.toUpperCase()}</td><td className="text-right">{usd(r.amount)}</td>
                <td className="text-xs">{new Date(r.accepted_at).toLocaleString()}</td>
                <td data-testid={`gtax-admin-kyc-${r.reference}`}>
                  <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-widest ${BADGE[r.kyc_status || "pending"]}`}>{r.kyc_status === "not_required" ? "no KYC" : r.kyc_status || "pending"}</span>
                  {r.channel === "telegram" && <div className="text-xs text-[color:var(--mute)] mt-1" data-testid={`gtax-admin-tg-${r.reference}`}>via Telegram{r.telegram_username ? ` · @${r.telegram_username}` : ""}</div>}
                  {r.kyc_source === "sumsub" && <div className="text-xs text-[color:var(--mute)] mt-1">via Sumsub</div>}
                  {r.approval_email && <div className="text-xs text-[color:var(--mute)] mt-1" data-testid={`gtax-admin-email-${r.reference}`}>{r.approval_email.ok ? "Welcome email sent" : "Welcome email failed"}</div>}
                </td>
                <td className="font-mono text-xs break-all max-w-[180px]">{r.txid || "—"}{r.deposits?.at(-1)?.network && <div className="text-[color:var(--mute)]">{r.deposits.at(-1).network}</div>}</td>
                <td className="whitespace-nowrap space-x-2">
                  {<>
                  <button disabled={r.kyc_status === "approved"} onClick={() => decide(r.reference, "approved")} className="gx-ghost !px-3 !py-1 disabled:opacity-30" data-testid={`gtax-admin-approve-${r.reference}`}>Approve</button>
                  <button disabled={r.kyc_status === "rejected"} onClick={() => window.confirm(`Reject ${r.full_name} (${r.reference})?`) && decide(r.reference, "rejected")} className="gx-ghost !px-3 !py-1 disabled:opacity-30" data-testid={`gtax-admin-reject-${r.reference}`}>Reject</button>
                  </>}
                  {r.kyc_status === "approved" && <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/access?ref=${r.reference}`).then(() => toast.success("Invite link copied"))} className="gx-ghost !px-3 !py-1" data-testid={`gtax-admin-invite-${r.reference}`}>Copy invite link</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <h2 className="gx-serif text-3xl">Contact messages ({contacts.length})</h2>
        <div className="mt-6 space-y-4">
          {contacts.map((c) => (
            <div key={c.id} className="gx-card p-5" data-testid={`gtax-admin-contact-${c.id}`}>
              <div className="text-sm"><b>{c.name}</b> · {c.email} · {new Date(c.created_at).toLocaleString()}</div>
              {c.subject && <div className="gx-caps text-sm mt-2">{c.subject}</div>}
              <p className="mt-2 text-[color:var(--mute)]">{c.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function GtaxAdmin() {
  const [token, setToken] = useState(sessionStorage.getItem("gtax_admin"));
  const [tab, setTab] = useState("members");
  const logout = () => { sessionStorage.removeItem("gtax_admin"); setToken(null); };
  return (
    <section className="pt-44 pb-28" data-testid="gtax-admin-page">
      <div className="gx-wrap">
        <p className="gx-eyebrow mb-8">Administration · two-factor secured</p>
        {token && (
          <div className="flex gap-2 mb-10" data-testid="gtax-admin-tabs">
            {[["members", "Members & KYC"], ["returns", "Returns engine"], ["telegram", "Telegram, deposits & withdrawals"]].map(([id, l]) => (
              <button key={id} onClick={() => setTab(id)} className={`gx-ghost ${tab === id ? "!border-[color:var(--gold)] !text-[color:var(--gold2)]" : ""}`} data-testid={`gtax-admin-tab-${id}`}>{l}</button>
            ))}
          </div>
        )}
        {!token ? <Login onAuthed={setToken} /> : tab === "members" ? <Dashboard token={token} logout={logout} /> : tab === "returns" ? <AdminReturns token={token} /> : <AdminTelegram token={token} />}
      </div>
    </section>
  );
}
